import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import {
  USCG_FOC_SE_ORG_ACTION_DESCRIPTION,
  USCG_FOC_SE_RESPONSE_CHECKLIST_LABELS,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/uscgFocSoutheastThreatNotification';
import {
  keys as orgActionKeys,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';

const ensureLocks = new Map<string, Promise<unknown>>();

async function ensureUscgFocSeThreatOrgActionsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<GetOrgActionResponse[]>(
    `${config.apiGateway.ics_org_actions}/${groupGuid}`
  );
  const existingNames = new Set(
    (resp.data ?? [])
      .filter((action) => !action.archived)
      .map((action) => action.name)
  );

  const missing = USCG_FOC_SE_RESPONSE_CHECKLIST_LABELS.filter(
    (label) => !existingNames.has(label)
  );
  if (missing.length === 0) {
    return;
  }

  await Promise.all(
    missing.map((name) =>
      axios.post(`${config.apiGateway.ics_org_actions}/${groupGuid}`, {
        name,
        description: USCG_FOC_SE_ORG_ACTION_DESCRIPTION,
        checklist: [],
        status: 'Planned',
        user_guid: userGuid,
        utc_offset: -new Date().getTimezoneOffset(),
      })
    )
  );

  await queryClient.invalidateQueries({
    queryKey: orgActionKeys.byGroupAndUser(groupGuid, userGuid),
  });
}

/**
 * When FOC is on, ensure the Southeast tropical Response Checklist labels
 * (including Review ACP & GRS) exist as Planned org actions.
 */
export function useEnsureUscgFocSeThreatOrgActions() {
  const focEnabled = useUscgFocRequirementsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureUscgFocSeThreatOrgActionsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[USCG FOC] Failed to ensure Response Checklist org actions',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [focEnabled, groupGuid, userGuid, queryClient]);
}
